import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Card, 
  Form, 
  Input, 
  InputNumber, 
  Button, 
  Table, 
  Space, 
  Modal, 
  message,
  Select,
  Tag,
  Spin,
  Alert
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useAdmin } from '../../hooks/useAdmin';

const { Title, Text } = Typography;
const { Option } = Select;

const MLMSettings = () => {
  
  const [levels, setLevels] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [levelModalVisible, setLevelModalVisible] = useState(false);
  const [rankModalVisible, setRankModalVisible] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [editingRank, setEditingRank] = useState(null);
  const [levelForm] = Form.useForm();
  const [rankForm] = Form.useForm();
  const [loading, setLoading] = useState({
    levels: false,
    ranks: false,
    save: false
  });
  const [error, setError] = useState(null);

  const { 
    getMLMLevels, 
    getMLMRanks, 
    createMLMLevel, 
    updateMLMLevel, 
    deleteMLMLevel,
    createMLMRank,
    updateMLMRank,
    deleteMLMRank
  } = useAdmin();

  const loadMLMData = useCallback(async () => {
    try {
      setError(null);
      setLoading(prev => ({ ...prev, levels: true, ranks: true }));
      
      const levelsResponse = await getMLMLevels();
      
      if (levelsResponse.success) {
        setLevels(levelsResponse.data || []);
      } else {
        console.error('❌ Levels error:', levelsResponse.message);
        message.error(levelsResponse.message || 'Erreur lors du chargement des niveaux');
      }
      
      const ranksResponse = await getMLMRanks();
      
      if (ranksResponse.success) {
        setRanks(ranksResponse.data || []);
      } else {
        console.error('❌ Ranks error:', ranksResponse.message);
        message.error(ranksResponse.message || 'Erreur lors du chargement des rangs');
      }
    } catch (error) {
      console.error('❌ loadMLMData error:', error);
      setError('Impossible de charger les données. Veuillez vérifier la connexion au serveur.');
      message.error('Erreur de connexion au serveur');
    } finally {
      setLoading(prev => ({ ...prev, levels: false, ranks: false }));
    }
  }, [getMLMLevels, getMLMRanks]);

  useEffect(() => {
    loadMLMData();
  }, [loadMLMData]);

  const handleAddLevel = () => {
    setEditingLevel(null);
    levelForm.resetFields();
    
    // Calculer le prochain numéro de niveau
    const nextLevelNumber = levels.length > 0 
      ? Math.max(...levels.map(l => l.level_number)) + 1 
      : 1;

    levelForm.setFieldsValue({
      level_number: nextLevelNumber,
      commission_percent: 10,
      min_team_required: 0,
      personal_volume_required: 0,
      status: 'active'
    });
    setLevelModalVisible(true);
  };

  const handleEditLevel = (level) => {
    setEditingLevel(level);
    levelForm.setFieldsValue({
      level_number: level.level_number,
      commission_percent: level.commission_percent,
      min_team_required: level.min_team_required,
      personal_volume_required: level.personal_volume_required,
      status: level.status
    });
    setLevelModalVisible(true);
  };

  const handleSaveLevel = async () => {
    try {
      const values = await levelForm.validateFields();
      
      setLoading(prev => ({ ...prev, save: true }));
      
      const dataToSend = {
        level_number: values.level_number,
        commission_percent: values.commission_percent,
        min_team_required: values.min_team_required || 0,
        personal_volume_required: values.personal_volume_required || 0,
        status: values.status || 'active'
      };
      
      
      let result;
      if (editingLevel) {
        result = await updateMLMLevel(editingLevel.id, dataToSend);
      } else {
        result = await createMLMLevel(dataToSend);
      }
      
      
      if (result.success) {
        message.success(editingLevel ? 'Niveau mis à jour avec succès' : 'Niveau ajouté avec succès');
        loadMLMData();
        setLevelModalVisible(false);
      } else {
        message.error(result.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('❌ Save level error:', error);
      message.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };

  const handleDeleteLevel = async (level) => {
    if (levels.length <= 1) {
      message.warning('Vous devez avoir au moins un niveau');
      return;
    }
    
    Modal.confirm({
      title: 'Supprimer le niveau',
      content: `Êtes-vous sûr de vouloir supprimer le niveau ${level.level_number}?`,
      async onOk() {
        try {
          const result = await deleteMLMLevel(level.id);
          if (result.success) {
            message.success('Niveau supprimé avec succès');
            loadMLMData();
          } else {
            message.error(result.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('❌ Delete error:', error);
          message.error('Erreur lors de la suppression');
        }
      },
    });
  };

  const handleAddRank = () => {
    setEditingRank(null);
    rankForm.resetFields();
    rankForm.setFieldsValue({
      rank_order: 1,
      min_personal_volume: 0,
      min_team_volume: 0,
      min_direct_referrals: 0,
      bonus_percent: 5,
      status: 'active'
    });
    setRankModalVisible(true);
  };

  const handleEditRank = (rank) => {
    setEditingRank(rank);
    rankForm.setFieldsValue(rank);
    setRankModalVisible(true);
  };

  const handleSaveRank = async () => {
    try {
      const values = await rankForm.validateFields();
      
      setLoading(prev => ({ ...prev, save: true }));
      
      let result;
      if (editingRank) {
        result = await updateMLMRank(editingRank.id, values);
      } else {
        result = await createMLMRank(values);
      }
      
      
      if (result.success) {
        message.success(editingRank ? 'Rang mis à jour avec succès' : 'Rang créé avec succès');
        loadMLMData();
        setRankModalVisible(false);
      } else {
        message.error(result.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('❌ Save rank error:', error);
      message.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };

  const handleDeleteRank = async (rank) => {
    Modal.confirm({
      title: 'Supprimer le rang',
      content: `Êtes-vous sûr de vouloir supprimer le rang "${rank.rank_name}"?`,
      async onOk() {
        try {
          const result = await deleteMLMRank(rank.id);
          if (result.success) {
            message.success('Rang supprimé avec succès');
            loadMLMData();
          } else {
            message.error(result.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('❌ Delete rank error:', error);
          message.error('Erreur lors de la suppression');
        }
      },
    });
  };

  const levelColumns = [
    {
      title: 'Niveau',
      dataIndex: 'level_number',
      key: 'level_number',
      width: 100,
      sorter: (a, b) => a.level_number - b.level_number,
    },
    {
      title: 'Commission %',
      dataIndex: 'commission_percent',
      key: 'commission_percent',
      render: (percent) => `${percent}%`,
    },
    {
      title: 'Équipe Minimum',
      dataIndex: 'min_team_required',
      key: 'min_team_required',
      render: (value) => value || '0',
    },
    {
      title: 'Volume Personnel Minimum',
      dataIndex: 'personal_volume_required',
      key: 'personal_volume_required',
      render: (value) => `$${value || '0'}`,
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? 'Actif' : 'Inactif'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => handleEditLevel(record)}
          />
          <Button 
            type="link" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteLevel(record)}
          />
        </Space>
      ),
    },
  ];

  const rankColumns = [
    {
      title: 'Nom',
      dataIndex: 'rank_name',
      key: 'rank_name',
    },
    {
      title: 'Ordre',
      dataIndex: 'rank_order',
      key: 'rank_order',
      sorter: (a, b) => a.rank_order - b.rank_order,
    },
    {
      title: 'Volume Personnel Min',
      dataIndex: 'min_personal_volume',
      key: 'min_personal_volume',
      render: (value) => `$${value}`,
    },
    {
      title: 'Volume Équipe Min',
      dataIndex: 'min_team_volume',
      key: 'min_team_volume',
      render: (value) => `$${value}`,
    },
    {
      title: 'Parrainages Directs Min',
      dataIndex: 'min_direct_referrals',
      key: 'min_direct_referrals',
    },
    {
      title: 'Bonus %',
      dataIndex: 'bonus_percent',
      key: 'bonus_percent',
      render: (value) => `${value}%`,
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? 'Actif' : 'Inactif'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => handleEditRank(record)}
          />
          <Button 
            type="link" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteRank(record)}
          />
        </Space>
      ),
    },
  ];


  return (
    <div className="admin-mlm-inner animate-fade-in">
      <div className="glass-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2} className="admin-page-title">Configuration MLM</Title>
            <Text className="admin-page-subtitle">Définissez les commissions par niveaux et les conditions de passage de rang.</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={loadMLMData} className="glass-button">Actualiser</Button>
        </div>
      </div>
      
      {error && (
        <Alert
          message="Attention"
          description={error}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 24 }}
          onClose={() => setError(null)}
        />
      )}
      
      <Card 
        className="glass-card"
        title={<span style={{ color: '#fff' }}>Niveaux de Commission ({levels.length})</span>} 
        style={{ marginBottom: 32 }}
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddLevel}
            className="glass-button"
          >
            Ajouter Niveau
          </Button>
        }
      >
        <Spin spinning={loading.levels} tip="Chargement des niveaux...">
          <Table
            dataSource={levels}
            columns={levelColumns}
            rowKey="id"
            pagination={false}
            locale={{ emptyText: 'Aucun niveau configuré' }}
          />
        </Spin>
      </Card>
      
      <Card 
        className="glass-card"
        title={<span style={{ color: '#fff' }}>Rangs MLM ({ranks.length})</span>} 
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddRank}
            className="glass-button"
          >
            Ajouter Rang
          </Button>
        }
      >
        <Spin spinning={loading.ranks} tip="Chargement des rangs...">
          <Table
            dataSource={ranks}
            columns={rankColumns}
            rowKey="id"
            pagination={false}
            locale={{ emptyText: 'Aucun rang configuré' }}
          />
        </Spin>
      </Card>

      {/* Modal pour niveaux */}
      <Modal
        title={editingLevel ? `Modifier Niveau ${editingLevel.level_number}` : "Ajouter Niveau"}
        open={levelModalVisible}
        onCancel={() => {
          setLevelModalVisible(false);
        }}
        onOk={handleSaveLevel}
        confirmLoading={loading.save}
        width={600}
      >
        <Form form={levelForm} layout="vertical">
          <Form.Item 
            name="level_number" 
            label="Numéro du Niveau"
            rules={[{ required: true, message: 'Veuillez entrer le numéro du niveau' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} disabled={!!editingLevel} />
          </Form.Item>
          
          <Form.Item
            name="commission_percent"
            label="Pourcentage de Commission"
            rules={[{ required: true, message: 'Veuillez entrer un pourcentage' }]}
          >
            <InputNumber 
              min={0} 
              max={100} 
              style={{ width: '100%' }} 
              addonAfter="%" 
            />
          </Form.Item>
          
          <Form.Item
            name="min_team_required"
            label="Équipe Minimum Requise"
          >
            <InputNumber 
              min={0} 
              style={{ width: '100%' }} 
            />
          </Form.Item>
          
          <Form.Item
            name="personal_volume_required"
            label="Volume Personnel Minimum ($)"
          >
            <InputNumber 
              min={0} 
              style={{ width: '100%' }} 
              prefix="$"
            />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="Statut"
            initialValue="active"
          >
            <Select>
              <Option value="active">Actif</Option>
              <Option value="inactive">Inactif</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal pour rangs */}
      <Modal
        title={editingRank ? `Modifier Rang "${editingRank.rank_name}"` : "Ajouter Rang"}
        open={rankModalVisible}
        onCancel={() => {
          setRankModalVisible(false);
        }}
        onOk={handleSaveRank}
        confirmLoading={loading.save}
        width={600}
      >
        <Form form={rankForm} layout="vertical">
          <Form.Item
            name="rank_name"
            label="Nom du Rang"
            rules={[{ required: true, message: 'Veuillez entrer un nom' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="rank_order"
            label="Ordre"
            rules={[{ required: true, message: 'Veuillez entrer un ordre' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="min_personal_volume"
            label="Volume Personnel Minimum ($)"
          >
            <InputNumber min={0} style={{ width: '100%' }} prefix="$" />
          </Form.Item>
          
          <Form.Item
            name="min_team_volume"
            label="Volume Équipe Minimum ($)"
          >
            <InputNumber min={0} style={{ width: '100%' }} prefix="$" />
          </Form.Item>
          
          <Form.Item
            name="min_direct_referrals"
            label="Parrainages Directs Minimum"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="bonus_percent"
            label="Pourcentage de Bonus"
          >
            <InputNumber 
              min={0} 
              max={100} 
              style={{ width: '100%' }} 
              addonAfter="%" 
            />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="Statut"
            initialValue="active"
          >
            <Select>
              <Option value="active">Actif</Option>
              <Option value="inactive">Inactif</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MLMSettings;